import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Avatar, IconButton, Toolbar } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useAuth } from '../contexts/AuthContext';
import usePermissions from '../hooks/usePermissions';
import { PERMS } from '../config/pmPermissions';
import { getInitialsFromText } from '../utils/initials';
import OrganizationSwitcher from './OrganizationSwitcher';

function OrganizationSidebarHeader({ collapsed = false }) {
    const { t } = useTranslation();
    const { orgId } = useParams();
    const { organization } = useAuth();
    const { has } = usePermissions();

    const initials = useMemo(
        () => getInitialsFromText(organization?.name),
        [organization?.name],
    );

    const settingsPath = `/app/${orgId}/settings`;
    const showSettings = has(PERMS.ORGANIZATIONS_EDIT);

    if (collapsed) {
        return (
            <Toolbar
                sx={{
                    px: 1,
                    minHeight: { xs: 56, sm: 64 },
                    justifyContent: 'center',
                }}
            >
                <IconButton
                    component={showSettings ? Link : 'div'}
                    to={showSettings ? settingsPath : undefined}
                    aria-label={t('app.layout.organizationSettings')}
                    title={t('app.layout.organizationSettings')}
                    sx={{ p: 0.5 }}
                    disabled={!showSettings}
                >
                    <Avatar
                        sx={{
                            width: 36,
                            height: 36,
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            fontSize: '0.875rem',
                            fontWeight: 700,
                        }}
                    >
                        {initials}
                    </Avatar>
                </IconButton>
            </Toolbar>
        );
    }

    return (
        <Toolbar
            sx={{
                px: 1,
                gap: 0.5,
                minHeight: { xs: 56, sm: 64 },
                overflow: 'visible',
            }}
        >
            <OrganizationSwitcher variant="toolbar" />
            <IconButton
                component={showSettings ? Link : 'div'}
                to={showSettings ? settingsPath : undefined}
                size="small"
                aria-label={t('app.layout.organizationSettings')}
                title={t('app.layout.organizationSettings')}
                sx={{ flexShrink: 0 }}
                disabled={!showSettings}
            >
                <SettingsIcon fontSize="small" />
            </IconButton>
        </Toolbar>
    );
}

export default OrganizationSidebarHeader;
